/**
 * Quiz IRS Generator
 * 生成 IRS 互動配置（msgBody 和 imgArr.idArr）
 */
export class QuizIRSGenerator {
  /**
   * 根據題型獲取 IRS 模式
   * @param {Object} question - 題目對象
   * @returns {String} IRS 模式
   */
  getIRSModel(question) {
    switch (question.type) {
      case 'single':
      case 'judge':
        return 'irsImmediately';

      case 'multiple':
        return 'irsImmediately'; // 多選也用 irsImmediately

      case 'complete':
      case 'subjective':
        return 'irsText';

      default:
        return 'normal';
    }
  }

  /**
   * 格式化答案
   * @param {Object} question - 題目對象
   * @returns {String|Array} 格式化後的答案
   */
  formatAnswer(question) {
    // irsImmediately 類型（single/judge/multiple）統一回傳陣列，irsText 回傳字串
    const isChoiceType = question.type === 'single' || question.type === 'judge' || question.type === 'multiple';

    if (!question.answer) {
      return isChoiceType ? [] : '';
    }

    if (isChoiceType) {
      return question.answer.split('');
    }

    return question.answer;
  }

  /**
   * 獲取字母選項
   * @param {Object} question - 題目對象
   * @returns {Array} 字母選項數組
   */
  getLetterSelect(question) {
    // 是非題
    if (question.type === 'judge') {
      return ['A', 'B']; // A=是, B=非
    }

    // 單選或多選：根據選項數量生成 A, B, C, D...
    if (question.options && question.options.length > 0) {
      return question.options.map((_, i) => String.fromCharCode(65 + i));
    }

    // 文字題：無選項
    return [];
  }

  /**
   * 獲取數字選項
   * @param {Object} question - 題目對象
   * @returns {Array} 數字選項數組
   */
  getNumberSelect(question) {
    if (question.options && question.options.length > 0) {
      return question.options.map((_, i) => String(i + 1));
    }
    return [];
  }

  /**
   * 初始化學生 IRS 數據
   * @param {Array} students - 學生數組
   * @returns {Array} 初始化的學生 IRS 數據
   */
  initStudentsIRS(students) {
    if (!students || students.length === 0) {
      return [];
    }

    // 為了與 Board.vue 現有 IRS 流程相容，這裡產生的結構必須與
    // this.$store.state.students 中的學生物件一致，只是清空作答相關欄位。
    return students.map(student => ({
      // 身分與基本資訊（盡量沿用既有欄位）
      accountid: student.accountid || student.AccountId || student.studentId || '',
      id: student.id,
      studentName: student.studentName || student.StudentName || student.name || '',
      headImg: student.headImg,
      status: student.status,
      sort: student.sort,

      // 作答相關欄位：統一初始化為「未作答」狀態
      option: '',
      statement: '',
      score: 0,
      rank: '',
      class: 'studyboxone',
      showcardclass: 'brand',
      answerclass: 'studyboxone',
      answerbrand: 'brand',

      // 保留其他可能用到的欄位（若原物件上有）
      seatNo: student.seatNo,
      group: student.group,
      SeatNum: student.SeatNum,
      StudentId: student.StudentId,
      StudentName: student.StudentName
    }));
  }

  /**
   * 初始化學生文字回應數據
   * @param {Array} students - 學生數組
   * @returns {Array} 初始化的學生文字數據
   */
  initStudentsText(students) {
    if (!students || students.length === 0) {
      return [];
    }

    return students.map(student => ({
      studentId: student.StudentId || student.id || '',
      studentName: student.StudentName || student.name || '',
      text: '',
      timestamp: null
    }));
  }

  /**
   * 生成 msgBody 數據項
   * @param {Object} question - 題目對象
   * @param {Number} pageIndex - 頁碼索引
   * @param {String} pageId - 頁面 UUID
   * @param {Array} students - 學生數組
   * @returns {Object} msgBody 數據項
   */
  generateMsgBodyData(question, pageIndex, pageId, students) {
    const irsModel = this.getIRSModel(question);
    const correctanswer = this.formatAnswer(question);

    // Excel 匯入時正解已經確定，若有填寫正解則直接視為已生效（answerstatus=1），
    // 讓學生一作答就能立即比對計分，不需老師再手動點一次「設定正確答案」。
    // （AnswerShowhide 預設為隱藏遮罩，不會因此提前洩漏正解給學生）
    const hasCorrectAnswer = Array.isArray(correctanswer) ? correctanswer.length > 0 : correctanswer !== '';

    return {
      pageid: pageId,
      page: pageIndex + 1,
      irsmodel: irsModel,

      // 答案相關
      correctanswer,
      pattern: 'letter',
      letterselect: this.getLetterSelect(question),
      numberselect: this.getNumberSelect(question),

      // 學生數據
      totalirs: irsModel === 'irsImmediately' ? this.initStudentsIRS(students) : [],
      totaltext: irsModel === 'irsText' ? this.initStudentsText(students) : [],

      // 狀態
      StudentPercentage: 0,
      // 0:未開始 1:作答中(正解已生效可計分) 2:已結束
      answerstatus: (irsModel === 'irsImmediately' || irsModel === 'irsText') && hasCorrectAnswer ? 1 : 0,

      // 其他
      workData: [],
      currentWorkNum: 0,
      buzzInStudents: []
    };
  }

  /**
   * 生成 imgArr.idArr 數據項
   * @param {Number} pageIndex - 頁碼索引
   * @param {String} pageId - 頁面 UUID
   * @param {String} irsModel - IRS 模式
   * @returns {Object} imgArr.idArr 數據項
   */
  generateImgArrIdData(pageIndex, pageId, irsModel) {
    return {
      pageid: pageId,
      pagetype: irsModel === 'normal' ? 'Normal' : 'Quiz',
      page: pageIndex + 1
    };
  }
}
